import React, { useCallback } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { useDownloadSubscriptionDocument } from '../../api/subscriptions';
import { BankTransferDetails, PendingTransfer } from '../../types/subscriptions';
import { formatDate, formatNumber } from '../../utils';
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from '../../consts';
import styles from './bankTransferDetails.module.css';

interface FieldProps {
  label: string;
  value: string;
  /** Copied instead of the displayed value, e.g. the bare amount. */
  copyValue?: string;
  onCopy?: (value: string) => void;
}

const Field: React.FC<FieldProps> = ({ label, value, copyValue, onCopy }) => (
  <Box className={styles.field}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Box className={styles.value}>
      <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}>
        {value}
      </Typography>
      {onCopy && (
        <IconButton size="small" aria-label={label} onClick={() => onCopy(copyValue ?? value)}>
          <ContentCopyIcon fontSize="inherit" />
        </IconButton>
      )}
    </Box>
  </Box>
);

interface BankTransferDetailsPanelProps {
  details: BankTransferDetails | null;
  transfers: PendingTransfer[];
}

/** What to transfer and where: each unpaid charge with its own title, then the account. */
export const BankTransferDetailsPanel: React.FC<BankTransferDetailsPanelProps> = ({
  details,
  transfers,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { download, loading: downloading } = useDownloadSubscriptionDocument();

  const downloadProforma = async (transfer: PendingTransfer) => {
    if (!transfer.proforma_id || !transfer.proforma_number) {
      return;
    }
    const { success } = await download(transfer.proforma_id, transfer.proforma_number);
    if (!success) {
      enqueueSnackbar(t('Could not download the document. Please try again.'), ERROR_SNACKBAR);
    }
  };

  const copy = useCallback(
    async (value: string) => {
      try {
        await navigator.clipboard.writeText(value);
        enqueueSnackbar(t('Copied to clipboard'), SUCCESS_SNACKBAR);
      } catch {
        // Clipboard unavailable (insecure context) - the value stays selectable.
      }
    },
    [enqueueSnackbar, t]
  );

  const kindLabel: Record<PendingTransfer['kind'], string> = {
    SUBSCRIPTION: t('Subscription fee'),
    PRORATION: t('Plan upgrade surcharge'),
    FINAL: t('Final settlement'),
  };

  return (
    <Box className={styles.container}>
      {transfers.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('Nothing to pay right now. We will e-mail the transfer details whenever a payment is due.')}
        </Typography>
      ) : (
        transfers.map((transfer) => (
          <Box key={transfer.id} className={styles.transfer}>
            <Box className={styles.transferHeader}>
              <Typography variant="subtitle2">
                {kindLabel[transfer.kind]}
                {transfer.billing_period_start &&
                  ` — ${dayjs(transfer.billing_period_start).format('MM.YYYY')}`}
              </Typography>
              {transfer.is_overdue && <Chip size="small" color="error" label={t('Overdue')} />}
            </Box>
            <Field
              label={t('Amount')}
              value={`${formatNumber(transfer.amount)} ${transfer.currency}`}
              // Ungrouped ("1234,56"), so it pastes cleanly into a bank form.
              copyValue={formatNumber(transfer.amount).replace(/[\s  ]/g, '')}
              onCopy={copy}
            />
            <Field label={t('Transfer title')} value={transfer.title} onCopy={copy} />
            <Field label={t('Due date')} value={formatDate(transfer.due_date)} />
            {transfer.proforma_id && (
              <Box>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<DownloadIcon />}
                  disabled={downloading}
                  onClick={() => downloadProforma(transfer)}
                >
                  {t('Download proforma {{number}}', { number: transfer.proforma_number })}
                </Button>
              </Box>
            )}
          </Box>
        ))
      )}

      {details && (
        <>
          <Field
            label={t('Recipient')}
            value={[details.recipient_name, details.recipient_address].filter(Boolean).join('\n')}
          />
          <Field label={t('Account number')} value={details.account_number} onCopy={copy} />
          {details.bank_name && <Field label={t('Bank')} value={details.bank_name} />}
          {details.instructions && <Alert severity="info">{details.instructions}</Alert>}
        </>
      )}

      <Typography variant="caption" color="text.secondary">
        {t('Use the exact transfer title so we can match your payment. We send the invoice once the transfer is booked.')}
      </Typography>
    </Box>
  );
};
